import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast, Toaster } from "sonner";
import axios from "axios";
import {
  ChevronRight, ChevronLeft, CheckCircle, Send, Loader2,
  ClipboardList, BarChart3, AlertTriangle, Lightbulb, Wrench, MessageSquare,
  Monitor, Target, UserCheck, XCircle
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_BASE}/api` });

// ========== SALES FORM CONFIG ==========
const SALES_SECTIONS = [
  { id: "header", title: "Your Details", icon: ClipboardList, color: "bg-slate-600" },
  { id: "A", title: "Lead Quality & Pipeline", icon: BarChart3, color: "bg-blue-600" },
  { id: "B", title: "Difficulties While Selling", icon: AlertTriangle, color: "bg-amber-600" },
  { id: "C", title: "Product Knowledge Gaps", icon: Lightbulb, color: "bg-purple-600" },
  { id: "D", title: "Process Gaps & Time Management", icon: Wrench, color: "bg-emerald-600" },
  { id: "E", title: "Support, Tools & Management", icon: Wrench, color: "bg-sky-600" },
  { id: "F", title: "Open Reflection", icon: MessageSquare, color: "bg-rose-600" },
];
const CUSTOMER_SECTIONS = [
  { id: "1", title: "Demo Experience", icon: Monitor, color: "bg-blue-600" },
  { id: "2", title: "Buying Intent & Readiness", icon: Target, color: "bg-amber-600" },
  { id: "3", title: "Pre Sales Consultant & Next Step", icon: UserCheck, color: "bg-emerald-600" },
];
const QUARTER_OPTIONS = ["Q1", "Q2", "Q3", "Q4", "All 4 quarters"];
const BLOCKER_OPTIONS = [
  "Budget — we need to confirm internal approval or allocation",
  "Need to see more features or attend a deeper focused demo",
  "Need to involve other decision makers or stakeholders from my team",
  "Want to compare ABSOLIN with other vendors first",
  "Concerns about implementation time, effort, or disruption to current operations",
];

// ========== SHARED COMPONENTS ==========
const RatingScale = ({ value, onChange, leftLabel, rightLabel, testId }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 sm:gap-3 justify-center">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(String(n))}
          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl text-base font-bold transition-all duration-200 border-2 ${
            String(value) === String(n)
              ? "bg-[#0066CC] text-white border-[#0066CC] scale-110 shadow-lg shadow-blue-200"
              : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50"
          }`} data-testid={`${testId}-${n}`}>{n}</button>
      ))}
    </div>
    <div className="flex justify-between text-xs text-slate-400 px-1">
      <span>{leftLabel}</span><span>{rightLabel}</span>
    </div>
  </div>
);
const MCQ = ({ options, value, onChange, testId }) => (
  <div className="space-y-2">
    {options.map((opt, i) => (
      <button key={i} type="button" onClick={() => onChange(opt)}
        className={`w-full text-left p-3 rounded-xl border-2 transition-all text-sm leading-relaxed ${
          value === opt ? "border-[#0066CC] bg-blue-50 text-[#0066CC] font-medium" : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-slate-50"
        }`} data-testid={`${testId}-opt-${i}`}>{opt}</button>
    ))}
  </div>
);
const YesNo = ({ value, onChange, testId }) => (
  <div className="flex gap-3">
    {["Yes", "No"].map((opt) => (
      <button key={opt} type="button" onClick={() => onChange(opt)}
        className={`px-6 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
          value === opt ? "border-[#0066CC] bg-blue-50 text-[#0066CC]" : "border-slate-200 bg-white text-slate-700 hover:border-blue-200"
        }`} data-testid={`${testId}-${opt.toLowerCase()}`}>{opt}</button>
    ))}
  </div>
);
const QCard = ({ number, question, required, hint, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm">
    <div className="mb-4">
      <div className="flex items-start gap-2">
        {number && <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 text-xs font-bold text-slate-600 shrink-0 mt-0.5">{number}</span>}
        <div>
          <p className="text-sm font-medium text-slate-800 leading-relaxed">{question} {required && <span className="text-red-500">*</span>}</p>
          {hint && <p className="text-xs text-slate-400 mt-1 italic">{hint}</p>}
        </div>
      </div>
    </div>
    {children}
  </div>
);

// ========== MAIN COMPONENT ==========
export default function FeedbackFormPublic() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [currentSection, setCurrentSection] = useState(0);
  const [userEmail, setUserEmail] = useState(emailParam);
  const [userName, setUserName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [quarter, setQuarter] = useState("");
  const [responses, setResponses] = useState({ q5_blockers: [] });

  useEffect(() => {
    loadCampaign();
  }, [token, emailParam]);

  const loadCampaign = async () => {
    try {
      const res = await api.get(`/feedback/form/${token}`, { params: { email: emailParam } });
      setCampaign(res.data);
      if (res.data.user_info) {
        setUserEmail(res.data.user_info.email);
        setUserName(res.data.user_info.name);
      }
      if (res.data.already_submitted) {
        setAlreadySubmitted(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Feedback form not found");
    } finally {
      setLoading(false);
    }
  };

  const setR = (key, val) => setResponses((prev) => ({ ...prev, [key]: val }));
  const toggleBlocker = (b) => setResponses((prev) => {
    const cur = prev.q5_blockers || [];
    return { ...prev, q5_blockers: cur.includes(b) ? cur.filter((x) => x !== b) : [...cur, b] };
  });

  const sections = campaign?.type === "sales" ? SALES_SECTIONS : CUSTOMER_SECTIONS;
  const section = sections[currentSection];
  const progress = ((currentSection + 1) / sections.length) * 100;

  const handleNext = () => {
    // Validate email selection on first section
    if (currentSection === 0 && campaign?.type === "sales" && !quarter) {
      toast.error("Please select the quarter"); return;
    }
    if (currentSection < sections.length - 1) { setCurrentSection((s) => s + 1); window.scrollTo(0, 0); }
  };
  const handlePrev = () => { if (currentSection > 0) { setCurrentSection((s) => s - 1); window.scrollTo(0, 0); } };

  const handleSubmit = async () => {
    if (!userEmail) { toast.error("Email is required"); return; }
    setSubmitting(true);
    try {
      const payload = { email: userEmail, full_name: userName, responses };
      if (campaign.type === "sales") payload.quarter_most_active = quarter;
      if (campaign.type === "customer") { payload.prospect_name = userName; payload.company_name = companyName; }
      await api.post(`/feedback/submit/${token}`, payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  // ========== RENDER STATES ==========
  if (loading) return <Shell><Loader2 className="w-8 h-8 animate-spin text-[#0066CC] mx-auto" /></Shell>;
  if (error) return <Shell><div className="text-center"><XCircle className="w-16 h-16 text-red-300 mx-auto mb-4" /><h2 className="text-xl font-bold text-slate-800 mb-2">Form Not Available</h2><p className="text-slate-500">{error}</p></div></Shell>;
  if (alreadySubmitted || submitted) return <Shell><div className="text-center"><div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-emerald-600" /></div><h2 className="text-2xl font-bold text-slate-900 mb-3">Thank You!</h2><p className="text-slate-500">Your feedback for <strong>"{campaign?.title}"</strong> has been recorded.</p></div></Shell>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />
      {/* Minimal Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/absolin-logo-transparent.png" alt="ABSOLIN" className="h-8" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="h-6 w-px bg-slate-200" />
            <span className="text-sm font-medium text-slate-600">{campaign.type === "sales" ? "Sales Team Feedback" : "Demo Experience Feedback"}</span>
          </div>
          <span className="text-xs text-slate-400">Confidential</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="feedback-title">{campaign.title}</h1>
          <p className="text-sm text-slate-500 mt-2">{campaign.type === "sales" ? "FY 2025-26 | Internal Use Only" : "Takes under 3 minutes | Confidential"}</p>
        </div>

        {/* Intro for customer */}
        {campaign.type === "customer" && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-sm text-slate-600 leading-relaxed">
            Thank you for taking the time to attend today's demo. This short form helps us understand how well the session met your expectations and what your next steps might be.
          </div>
        )}

        {/* User info (auto-filled) */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Name</Label>
              <p className="text-sm font-medium text-slate-900 mt-1" data-testid="auto-name">{userName || "—"}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Email</Label>
              <p className="text-sm font-medium text-slate-900 mt-1" data-testid="auto-email">{userEmail || "—"}</p>
            </div>
          </div>
          {!userEmail && (
            <div className="mt-4">
              <Label className="text-xs text-slate-500">Select your email to continue *</Label>
              <select
                className="w-full mt-1 border border-slate-200 rounded-lg p-2 text-sm"
                value={userEmail}
                onChange={(e) => {
                  setUserEmail(e.target.value);
                  const u = campaign.valid_emails?.find((em) => em === e.target.value);
                  if (u) setUserName(e.target.value.split("@")[0].replace(/[._]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()));
                }}
                data-testid="email-select"
              >
                <option value="">-- Select your email --</option>
                {(campaign.valid_emails || []).map((em) => <option key={em} value={em}>{em}</option>)}
              </select>
            </div>
          )}
          {campaign.type === "customer" && (
            <div className="mt-4">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Company Name</Label>
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Your company" className="mt-1" data-testid="input-company" />
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-500">Section {currentSection + 1} of {sections.length}</span>
            <span className="text-xs font-medium text-[#0066CC]">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#0066CC] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
          </div>
        </div>

        {/* Section Indicator */}
        <div className="flex items-center gap-3 mb-6 bg-white rounded-xl border border-slate-200 p-4">
          <div className={`w-10 h-10 rounded-lg ${section.color} flex items-center justify-center shrink-0`}>
            <section.icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{section.id === "header" ? "Start" : `Section ${section.id}`}</p>
            <h2 className="text-base font-semibold text-slate-900">{section.title}</h2>
          </div>
        </div>

        {/* QUESTIONS */}
        <div className="space-y-4">
          {/* ============ SALES QUESTIONS ============ */}
          {campaign.type === "sales" && currentSection === 0 && (
            <QCard question="Quarter Most Active" required>
              <MCQ options={QUARTER_OPTIONS} value={quarter} onChange={setQuarter} testId="quarter" />
            </QCard>
          )}
          {campaign.type === "sales" && currentSection === 1 && (<>
            <QCard number="Q1" question="How would you rate the overall quality of leads provided from the database — were they the right companies and right contacts to approach?" required>
              <RatingScale value={responses.q1} onChange={(v) => setR("q1", v)} leftLabel="Very poor quality" rightLabel="Excellent quality" testId="q1" />
            </QCard>
            <QCard number="Q2" question="At which stage of the 11-step sales process did most of your leads go cold or drop off?" required>
              <MCQ options={["Steps 2–3 — After first outbound call or email","Steps 4–5 — After follow-up call or info pack was sent","Step 7 — After the alignment call and 28-question survey","Step 10 — After the POC / product demo","Step 11 — After the project proposal was sent"]} value={responses.q2} onChange={(v) => setR("q2", v)} testId="q2" />
            </QCard>
            <QCard number="Q3" question="On average, how many qualified prospects were you actively managing at the same time?" required>
              <MCQ options={["1 to 5 active prospects","6 to 10 active prospects","11 to 20 active prospects","More than 20 — difficult to keep track of all of them"]} value={responses.q3} onChange={(v) => setR("q3", v)} testId="q3" />
            </QCard>
          </>)}
          {campaign.type === "sales" && currentSection === 2 && (<>
            <QCard number="Q4" question="What was the most common objection you received from prospects?" required>
              <MCQ options={['"Your price is too high compared to other options"','"We are already using another software and are satisfied"','"We are not ready to implement right now"','"We need approval from our management or IT department"','"We do not clearly see the ROI or value for our business"']} value={responses.q4} onChange={(v) => setR("q4", v)} testId="q4" />
              <div className="mt-3"><Label className="text-xs text-slate-500">Other (optional)</Label><Input value={responses.q4_other || ""} onChange={(e) => setR("q4_other", e.target.value)} placeholder="Other objection..." className="mt-1" data-testid="q4-other" /></div>
            </QCard>
            <QCard number="Q5" question="How confident were you in handling pricing objections and justifying ABSOLIN's value?" required>
              <RatingScale value={responses.q5} onChange={(v) => setR("q5", v)} leftLabel="Not confident" rightLabel="Very confident" testId="q5" />
            </QCard>
            <QCard number="Q6" question="Did you lose a deal to a competitor this year?" required>
              <YesNo value={responses.q6} onChange={(v) => setR("q6", v)} testId="q6" />
              {responses.q6 === "Yes" && <div className="mt-3"><Textarea value={responses.q6_details || ""} onChange={(e) => setR("q6_details", e.target.value)} placeholder="Competitor name and reason..." rows={3} className="mt-1" data-testid="q6-details" /></div>}
            </QCard>
            <QCard number="Q7" question="How difficult was it to reach the Key Decision Maker (KDM)?" required>
              <RatingScale value={responses.q7} onChange={(v) => setR("q7", v)} leftLabel="Very easy" rightLabel="Extremely difficult" testId="q7" />
            </QCard>
            <QCard number="Q8" question="How well was the POC demo customized to each prospect?" required>
              <MCQ options={["Always customized — prospects were engaged","Partially customized — could have been more specific","Mostly generic — same standard demo for everyone","Not enough control over demo content to customize"]} value={responses.q8} onChange={(v) => setR("q8", v)} testId="q8" />
            </QCard>
          </>)}
          {campaign.type === "sales" && currentSection === 3 && (<>
            <QCard number="Q9" question="Which ABSOLIN product did you feel least confident explaining?" required>
              <MCQ options={["Focus Softnet ERP","Odoo ERP","Vendor / Asset Management","Weigh Bridge / Project / Order Management","HRMS"]} value={responses.q9} onChange={(v) => setR("q9", v)} testId="q9" />
              <div className="mt-3"><Input value={responses.q9_details || ""} onChange={(e) => setR("q9_details", e.target.value)} placeholder="What made you less confident?" className="mt-1" data-testid="q9-details" /></div>
            </QCard>
            <QCard number="Q10" question="How sufficient was the product training you received?" required>
              <RatingScale value={responses.q10} onChange={(v) => setR("q10", v)} leftLabel="Very insufficient" rightLabel="Very sufficient" testId="q10" />
            </QCard>
            <QCard number="Q11" question="Did you have industry-specific case studies to share?" required>
              <YesNo value={responses.q11} onChange={(v) => setR("q11", v)} testId="q11" />
              {responses.q11 === "No" && <div className="mt-3"><Textarea value={responses.q11_details || ""} onChange={(e) => setR("q11_details", e.target.value)} placeholder="Missing industries/cases..." rows={3} className="mt-1" data-testid="q11-details" /></div>}
            </QCard>
          </>)}
          {campaign.type === "sales" && currentSection === 4 && (<>
            <QCard number="Q12" question="Did you consistently follow all 11 sales steps?" required>
              <YesNo value={responses.q12} onChange={(v) => setR("q12", v)} testId="q12" />
              {responses.q12 === "No" && <div className="mt-3"><Textarea value={responses.q12_details || ""} onChange={(e) => setR("q12_details", e.target.value)} placeholder="Steps skipped and reasons..." rows={3} className="mt-1" data-testid="q12-details" /></div>}
            </QCard>
            <QCard number="Q13" question="What percentage of your week was spent on non-selling activities?" required>
              <MCQ options={["Less than 20%","20 to 40%","40 to 60%","More than 60%"]} value={responses.q13} onChange={(v) => setR("q13", v)} testId="q13" />
            </QCard>
            <QCard number="Q14" question="How long did it take to get a proposal ready after an alignment call?" required>
              <MCQ options={["Same day or next day","2 to 3 days","4 to 7 days","More than a week"]} value={responses.q14} onChange={(v) => setR("q14", v)} testId="q14" />
            </QCard>
          </>)}
          {campaign.type === "sales" && currentSection === 5 && (<>
            <QCard number="Q15" question="How useful was the CRM for tracking follow-ups and pipeline?" required>
              <RatingScale value={responses.q15} onChange={(v) => setR("q15", v)} leftLabel="Not useful" rightLabel="Very useful" testId="q15" />
              <div className="mt-3"><Input value={responses.q15_details || ""} onChange={(e) => setR("q15_details", e.target.value)} placeholder="CRM improvement suggestion..." className="mt-1" data-testid="q15-details" /></div>
            </QCard>
            <QCard number="Q16" question="When stuck on a difficult deal, how easy was it to get management guidance?" required>
              <MCQ options={["Always available — practical guidance","Available but not always actionable","Difficult to get dedicated time","Had to figure it out on my own"]} value={responses.q16} onChange={(v) => setR("q16", v)} testId="q16" />
            </QCard>
            <QCard number="Q17" question="How effective were weekly sales review meetings?" required>
              <RatingScale value={responses.q17} onChange={(v) => setR("q17", v)} leftLabel="Not effective" rightLabel="Very effective" testId="q17" />
            </QCard>
            <QCard number="Q18" question="Did quarterly incentive schemes genuinely motivate you?" required>
              <MCQ options={["Yes — motivating with achievable targets","Somewhat — some reachable, others not","No — thresholds too high","No — prefer cash incentives"]} value={responses.q18} onChange={(v) => setR("q18", v)} testId="q18" />
            </QCard>
          </>)}
          {campaign.type === "sales" && currentSection === 6 && (<>
            <QCard number="Q19" question="What was the single hardest part of your job at ABSOLIN during FY 2025-26?" required>
              <Textarea value={responses.q19 || ""} onChange={(e) => setR("q19", e.target.value)} placeholder="Be specific..." rows={4} data-testid="q19" />
            </QCard>
            <QCard number="Q20" question="If you could change ONE thing about how ABSOLIN supports its sales team, what would it be?">
              <MCQ options={["Better demo scripts and case studies","More competitive pricing","Faster proposal turnaround","More 1-on-1 coaching","Clearer territory assignment","Better CRM/tools"]} value={responses.q20} onChange={(v) => setR("q20", v)} testId="q20" />
              <div className="mt-3"><Input value={responses.q20_details || ""} onChange={(e) => setR("q20_details", e.target.value)} placeholder="Additional detail..." className="mt-1" data-testid="q20-details" /></div>
            </QCard>
            <QCard number="Q21" question="Anything else for leadership?">
              <Textarea value={responses.q21 || ""} onChange={(e) => setR("q21", e.target.value)} placeholder="Your honest thoughts..." rows={4} data-testid="q21" />
            </QCard>
          </>)}

          {/* ============ CUSTOMER QUESTIONS ============ */}
          {campaign.type === "customer" && currentSection === 0 && (<>
            <QCard number="Q1" question="How would you rate your overall experience of today's product demo?" required>
              <RatingScale value={responses.q1} onChange={(v) => setR("q1", v)} leftLabel="Poor experience" rightLabel="Excellent experience" testId="q1" />
            </QCard>
            <QCard number="Q2" question="How relevant was today's demo to your specific business challenges?" required>
              <MCQ options={["Very relevant — addressed exactly what we face","Somewhat relevant — covered some areas but missed a few","Partially relevant — general, not specific to our industry","Not very relevant — did not connect with our situation"]} value={responses.q2} onChange={(v) => setR("q2", v)} testId="q2" />
            </QCard>
          </>)}
          {campaign.type === "customer" && currentSection === 1 && (<>
            <QCard number="Q3" question="After seeing today's demo, how likely are you to consider ABSOLIN?" required>
              <MCQ options={["Very likely — ready to move forward","Likely — need to evaluate further but genuinely interested","Unsure — need more time or information","Unlikely — not the right fit at this point"]} value={responses.q3} onChange={(v) => setR("q3", v)} testId="q3" />
            </QCard>
            <QCard number="Q4" question="What is your expected timeline to make a decision?" required>
              <MCQ options={["Within 2 weeks","Within 1 month","Within 1 to 3 months","More than 3 months","No timeline yet"]} value={responses.q4} onChange={(v) => setR("q4", v)} testId="q4" />
            </QCard>
            <QCard number="Q5" question="Is there anything holding you back from moving forward?" required>
              <YesNo value={responses.q5} onChange={(v) => setR("q5", v)} testId="q5" />
              {responses.q5 === "Yes" && (
                <div className="mt-4 space-y-3">
                  <Label className="text-xs text-slate-500 font-medium">Select all that apply:</Label>
                  {BLOCKER_OPTIONS.map((b, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Checkbox id={`bl-${i}`} checked={(responses.q5_blockers || []).includes(b)} onCheckedChange={() => toggleBlocker(b)} data-testid={`q5-blocker-${i}`} />
                      <label htmlFor={`bl-${i}`} className="text-sm text-slate-700 leading-relaxed cursor-pointer">{b}</label>
                    </div>
                  ))}
                  <div className="mt-3"><Textarea value={responses.q5_other || ""} onChange={(e) => setR("q5_other", e.target.value)} placeholder="Other concerns..." rows={3} data-testid="q5-other" /></div>
                </div>
              )}
            </QCard>
          </>)}
          {campaign.type === "customer" && currentSection === 2 && (<>
            <QCard number="Q6" question="How well did our Pre Sales Consultant understand your business?" required>
              <RatingScale value={responses.q6} onChange={(v) => setR("q6", v)} leftLabel="Poor understanding" rightLabel="Excellent understanding" testId="q6" />
            </QCard>
            <QCard number="Q7" question="What would be the most helpful next step for you?" required>
              <MCQ options={["Send me a detailed proposal with pricing","Schedule a follow-up call","Arrange a deeper demo on a specific module","Share customer case studies for my industry","Give me time — I will reach out when ready"]} value={responses.q7} onChange={(v) => setR("q7", v)} testId="q7" />
              <div className="mt-3"><Textarea value={responses.q7_details || ""} onChange={(e) => setR("q7_details", e.target.value)} placeholder="Specific topic for next interaction..." rows={3} data-testid="q7-details" /></div>
            </QCard>
          </>)}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          <Button variant="outline" onClick={handlePrev} disabled={currentSection === 0} className="gap-2" data-testid="prev-btn">
            <ChevronLeft className="w-4 h-4" /> Previous
          </Button>
          {currentSection < sections.length - 1 ? (
            <Button onClick={handleNext} className="gap-2 bg-[#0066CC] hover:bg-[#0052A3]" data-testid="next-btn">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? "Submitting..." : "Submit Feedback"}
            </Button>
          )}
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {sections.map((s, i) => (
            <button key={s.id} onClick={() => { if (i <= currentSection) setCurrentSection(i); }}
              className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentSection ? "bg-[#0066CC] scale-125" : i < currentSection ? "bg-blue-300" : "bg-slate-300"}`}
              data-testid={`dot-${i}`} />
          ))}
        </div>
      </div>

      {/* Minimal Footer */}
      <div className="border-t border-slate-200 mt-12 py-6 text-center text-xs text-slate-400">
        ABSOLIN Software Solutions | www.absolinsoft.com
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Toaster position="top-center" richColors />
      <div className="max-w-md w-full">{children}</div>
    </div>
  );
}
